import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ChampionshipDetail, ChampionshipManagerRow, PaginatedResult } from "@/types";
import { apiError, personName } from "@/lib/championships";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SearchSelect } from "@/components/ui/SearchSelect";
import { SortHeader, nextSort, type SortState } from "@/components/ui/SortHeader";

type SortKey = "nombre" | "email" | "club" | "fecha";

interface CandidateManager {
  id: number;
  nombre: string;
  apellidos: string | null;
  email: string;
  club: { id: number; nombre: string } | null;
}

const dateFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric" });

// Solo el organizador la ve: quién (de los usuarios de club) puede editar ESTE campeonato.
export function ManagersSection({ campeonato }: { campeonato: ChampionshipDetail }) {
  const queryClient = useQueryClient();
  const id = campeonato.id;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "fecha", dir: "desc" });
  const [candidate, setCandidate] = useState<CandidateManager | null>(null);
  const [revoking, setRevoking] = useState<ChampionshipManagerRow | null>(null);

  const filterParams = useMemo(() => {
    const params = new URLSearchParams({ sort: sort.key, dir: sort.dir });
    if (search) params.set("search", search);
    return params;
  }, [search, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ["championship", id, "managers", page, limit, filterParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(filterParams);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedResult<ChampionshipManagerRow>>(`/championships/${id}/managers?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["championship", id] });
  }

  const grant = useMutation({
    mutationFn: () => api.post(`/championships/${id}/managers`, { usuarioId: candidate!.id }),
    onSuccess: () => {
      setCandidate(null);
      refresh();
      toast.success("Permiso de gestión concedido");
    },
    onError: (error) => toast.error(apiError(error, "No se pudo conceder el permiso")),
  });

  const revoke = useMutation({
    mutationFn: (usuarioId: number) => api.delete(`/championships/${id}/managers/${usuarioId}`),
    onSuccess: () => {
      refresh();
      toast.success("Permiso de gestión revocado");
    },
    onError: (error) => toast.error(apiError(error, "No se pudo revocar el permiso")),
  });

  function toggleSort(key: SortKey) {
    setSort((s) => nextSort(s, key));
    setPage(1);
  }

  const rows = data?.data ?? [];

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
          <ShieldCheck className="h-4 w-4 text-brand-600" />
          Usuarios de club que pueden gestionar este campeonato ({campeonato.managersCount})
        </p>
        <p className="text-xs text-ink-400">
          Por defecto un usuario de club solo ve los campeonatos con partidos en su club. Aquí le das permiso para editar ESTE campeonato
          concreto (parejas, partidos, pistas, árbitros); no podrá eliminarlo ni dar permisos a otros.
        </p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <SearchSelect<CandidateManager>
            label="Conceder gestión a"
            value={candidate}
            onChange={setCandidate}
            queryKey={["championship", id, "candidates"]}
            fetchOptions={async (s) => (await api.get<CandidateManager[]>(`/championships/${id}/candidate-managers`, { params: { search: s } })).data}
            getKey={(u) => u.id}
            getLabel={(u) => personName(u)}
            getHint={(u) => `${u.email}${u.club ? ` · ${u.club.nombre}` : ""}`}
            placeholder="Busca un usuario de club…"
            searchPlaceholder="Nombre, email o club…"
          />
        </div>
        <Button onClick={() => grant.mutate()} disabled={!candidate || grant.isPending}>
          <Plus className="h-4 w-4" />
          Conceder
        </Button>
      </div>

      {campeonato.managersCount > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-1">
              <Input
                label="Buscar"
                placeholder="Nombre, email o club…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {searchInput ? (
              <Button
                onClick={() => {
                  setSearchInput("");
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            ) : (
              <span />
            )}
            <ExportExcelButton url={`/championships/${id}/managers/export.xlsx?${filterParams.toString()}`} filename="gestores-campeonato.xlsx" />
          </div>
        </>
      )}

      {isLoading ? (
        <TableSkeleton rows={2} cols={4} />
      ) : !rows.length ? (
        <p className="text-sm text-ink-400">{search ? "Ningún usuario coincide con la búsqueda." : "Nadie más tiene permiso de gestión todavía."}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
              <tr>
                <th className="px-3 py-3 sm:px-4">
                  <SortHeader label="Usuario" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                </th>
                <th className="hidden px-4 py-3 md:table-cell md:w-[28%]">
                  <SortHeader label="Email" active={sort.key === "email"} dir={sort.dir} onClick={() => toggleSort("email")} />
                </th>
                <th className="hidden px-4 py-3 md:table-cell md:w-[20%]">
                  <SortHeader label="Club" active={sort.key === "club"} dir={sort.dir} onClick={() => toggleSort("club")} />
                </th>
                <th className="hidden px-4 py-3 xl:table-cell xl:w-36">
                  <SortHeader label="Desde" active={sort.key === "fecha"} dir={sort.dir} onClick={() => toggleSort("fecha")} />
                </th>
                <th className="w-28 px-3 py-3 sm:px-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-t border-ink-100">
                  <td className="px-3 py-3 sm:px-4">
                    <p className="truncate font-medium text-ink-800" title={personName(m.usuario)}>
                      {personName(m.usuario)}
                    </p>
                    <p className="truncate text-xs text-ink-400 md:hidden">
                      {m.usuario.email}
                      {m.usuario.club && ` · ${m.usuario.club.nombre}`}
                    </p>
                  </td>
                  <td className="hidden truncate px-4 py-3 text-ink-500 md:table-cell" title={m.usuario.email}>
                    {m.usuario.email}
                  </td>
                  <td className="hidden truncate px-4 py-3 text-ink-500 md:table-cell" title={m.usuario.club?.nombre}>
                    {m.usuario.club?.nombre ?? "—"}
                  </td>
                  <td className="hidden px-4 py-3 whitespace-nowrap text-ink-500 xl:table-cell">{dateFormatter.format(new Date(m.createdAt))}</td>
                  <td className="px-3 py-3 text-right sm:px-4">
                    <Button onClick={() => setRevoking(m)}>Revocar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={data?.page ?? 1}
        totalPages={data?.totalPages ?? 1}
        total={data?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      {revoking && (
        <ConfirmDialog
          open={!!revoking}
          onOpenChange={(open) => !open && setRevoking(null)}
          title="Quitar permiso de gestión"
          description={`"${personName(revoking.usuario)}" dejará de poder editar este campeonato (seguirá viéndolo si tiene partidos en su club).`}
          tone="primary"
          confirmLabel="Revocar"
          onConfirm={() => revoke.mutate(revoking.usuarioId)}
        />
      )}
    </Card>
  );
}
